import {
  CompanyContainer,
  CompanyContentContainer,
  CompanyTabsContent,
  CompanyTabsList,
} from "components/company/v3/CompanyContainers";
import BrazilianAboutContent from "components/company/v3/brazilian/contents/about/BrazilianAboutContent";
import BrazilianBriefHistoryContent from "components/company/v3/brazilian/contents/about/BrazilianBriefHistoryContent";
import BrazilianRiskFactorsContent from "components/company/v3/brazilian/contents/about/BrazilianRiskFactorsContent";
import BrazilianSubsidiariesContent from "components/company/v3/brazilian/contents/about/BrazilianSubsidiariesContent";
import useBrazilianIssuer from "components/company/v3/context/hooks/useBrazilianIssuer";
import { CompanyAboutTabEnum } from "components/company/v3/menu/enums";
import { Tabs } from "components/ui/Tabs";
import useSelfie from "lib/hooks/useSelfie";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { Building2Icon, HistoryIcon, InfoIcon, ShieldAlertIcon } from "lucide-react";
import Link from "next/link";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface BrazilianAboutBoxProps {
  tickerSymbol: string;
  tab: CompanyAboutTabEnum;
  onChangeTab: (tab: CompanyAboutTabEnum) => void;
  children?: ReactNode;
}

export default function BrazilianAboutBox({ tickerSymbol, tab, onChangeTab, children }: BrazilianAboutBoxProps) {
  const intl = useIntl();
  const issuer = useBrazilianIssuer();
  const viewer = useSelfie();

  return (
    <CompanyContainer className="xl:h-full">
      <div className="flex w-full flex-col space-y-6">
        <CompanyContentContainer>
          <Tabs
            defaultValue={CompanyAboutTabEnum.ABOUT}
            value={tab}
            onValueChange={(value) => onChangeTab(value as CompanyAboutTabEnum)}
            className="w-full"
          >
            <CompanyTabsList
              tabs={[
                {
                  value: CompanyAboutTabEnum.ABOUT,
                  label: intl.formatMessage({
                    defaultMessage: "Sobre",
                    description: "Aba do menu superior da empresa",
                    id: "SqQ+WW",
                  }),
                  icon: <InfoIcon className="inline-flex" size={16} strokeWidth={2} />,
                },
                viewer?.isPremium
                  ? {
                      value: CompanyAboutTabEnum.HISTORY,
                      label: intl.formatMessage({
                        defaultMessage: "Histórico",
                        description: "Aba do menu superior da empresa",
                        id: "dcPi75",
                      }),
                      icon: <HistoryIcon className="inline-flex" size={16} strokeWidth={2} />,
                      disabled: !issuer.briefHistory && !issuer.briefHistoryUrl,
                    }
                  : null,
                viewer?.isPremium
                  ? {
                      value: CompanyAboutTabEnum.RISK_FACTORS,
                      label: intl.formatMessage({
                        defaultMessage: "Fatores de Risco",
                        id: "9DRmLP",
                        description: "Aba do menu superior da empresa",
                      }),
                      icon: <ShieldAlertIcon className="inline-flex" size={16} strokeWidth={2} />,
                      disabled: !issuer.riskFactors && !issuer.riskFactorsUrl,
                    }
                  : null,
                viewer?.isPremium
                  ? {
                      value: CompanyAboutTabEnum.SUBSIDIARIES,
                      label: intl.formatMessage({
                        defaultMessage: "Subsidiárias",
                        description: "Aba do menu superior da empresa",
                        id: "yztxBi",
                      }),
                      icon: <Building2Icon className="inline-flex" size={16} strokeWidth={2} />,
                      disabled: issuer.subsidiaries.length === 0,
                    }
                  : null,
              ].filter(notUndefinedOrNull)}
            />

            <CompanyTabsContent value={CompanyAboutTabEnum.ABOUT}>
              <BrazilianAboutContent tickerSymbol={tickerSymbol} />
            </CompanyTabsContent>

            <CompanyTabsContent value={CompanyAboutTabEnum.HISTORY}>
              {issuer.briefHistory ? (
                <BrazilianBriefHistoryContent />
              ) : (
                <Link
                  className="h-fit text-blue-500 underline"
                  href={issuer.briefHistoryUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <FormattedMessage defaultMessage="Link para o PDF com o Histórico da empresa" id="HPw70U" />
                </Link>
              )}
            </CompanyTabsContent>

            <CompanyTabsContent value={CompanyAboutTabEnum.RISK_FACTORS}>
              {issuer.riskFactors ? (
                <BrazilianRiskFactorsContent />
              ) : (
                <Link
                  className="h-fit text-blue-500 underline"
                  href={issuer.riskFactorsUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <FormattedMessage defaultMessage="Link para o PDF com os Fatores de Risco da empresa" id="8A9f6W" />
                </Link>
              )}
            </CompanyTabsContent>

            <CompanyTabsContent value={CompanyAboutTabEnum.SUBSIDIARIES}>
              <BrazilianSubsidiariesContent />
            </CompanyTabsContent>
          </Tabs>
        </CompanyContentContainer>

        {children}
      </div>
    </CompanyContainer>
  );
}
